import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, Stack, Typography } from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import CommitDrawer from "../CommitDrawer";
import type {
  PremiseCommit,
  PremiseCommitMessages,
} from "../../context/PremiseWorkspace";
import type { ActionKey } from "../toast";
import { patchPremise, premiseChanged } from "../../utils/premisePatch";

/**
 * Saving a template/start-view editor.
 *
 * These editors are written as "here is the premise as it should now be"
 * (`onChange(next)`). Two helpers turn that into the CMS's save model:
 *
 *  - {@link useCommitNext}: for editors whose every change is one discrete
 *    action (a switch, a reorder, a section's own Save) - the change is written
 *    at once and toasted.
 *  - {@link EditSectionCard}: for editors that are forms (typing into fields).
 *    The page shows a short card with an Edit button; the form lives in a
 *    drawer, on a COPY of the premise, and the drawer's Save writes it.
 *
 * Both write only what the edit changed (see `patchPremise`), so quick
 * successive edits cannot overwrite one another.
 */

export type CommitMessages = PremiseCommitMessages;

/**
 * `commitNext(next, messages)`: write the difference between `premise` (as the
 * page shows it) and `next`. Resolves true when stored (or when nothing
 * changed); a failure has already been toasted.
 */
export function useCommitNext(premise: PremiseConfig, onCommit: PremiseCommit) {
  return useCallback(
    (next: PremiseConfig, messages: CommitMessages): Promise<boolean> => {
      if (!premiseChanged(premise, next)) return Promise.resolve(true);
      return onCommit(patchPremise(premise, next), messages);
    },
    [premise, onCommit]
  );
}

export interface EditSectionCardProps {
  premise: PremiseConfig;
  onCommit: PremiseCommit;
  /** Names the section: "Branding & logo". Used in the button and drawer. */
  title: string;
  /** One or two sentences on what lives in the drawer. */
  description: ReactNode;
  /** The toast after a successful save. */
  success: string;
  /** Action key of the error toast. */
  failure: ActionKey;
  failureParams?: Record<string, string | number>;
  /** The form, given the working copy and its setter. */
  children: (
    local: PremiseConfig,
    setLocal: (next: PremiseConfig) => void
  ) => ReactNode;
  width?: number;
  "data-testid"?: string;
}

export function EditSectionCard({
  premise,
  onCommit,
  title,
  description,
  success,
  failure,
  failureParams,
  children,
  width = 760,
  "data-testid": testId,
}: EditSectionCardProps) {
  const { t } = useTranslation("templates");
  const [open, setOpen] = useState(false);
  return (
    <Card variant="outlined" data-testid={testId ?? "edit-section-card"}>
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {description}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen(true)}
            data-testid="edit-section-open"
            sx={{ flexShrink: 0 }}
          >
            {t("editSection.editTitle", { title: title.toLowerCase() })}
          </Button>
        </Stack>
      </CardContent>
      {open && (
        <EditSectionDrawer
          premise={premise}
          onCommit={onCommit}
          title={title}
          success={success}
          failure={failure}
          failureParams={failureParams}
          width={width}
          onClose={() => setOpen(false)}
        >
          {children}
        </EditSectionDrawer>
      )}
    </Card>
  );
}

function EditSectionDrawer({
  premise,
  onCommit,
  title,
  success,
  failure,
  failureParams,
  width,
  onClose,
  children,
}: Pick<
  EditSectionCardProps,
  | "premise"
  | "onCommit"
  | "title"
  | "success"
  | "failure"
  | "failureParams"
  | "children"
> & { width: number; onClose: () => void }) {
  // The working copy: Cancel throws it away, Save writes the difference.
  const { t } = useTranslation("templates");
  const [local, setLocal] = useState<PremiseConfig>(premise);
  return (
    <CommitDrawer
      title={t("editSection.editTitle", { title: title.toLowerCase() })}
      subtitle={premise.premiseName}
      width={width}
      onClose={onClose}
      saveDisabled={!premiseChanged(premise, local)}
      data-testid="edit-section-drawer"
      onSave={() =>
        onCommit(patchPremise(premise, local), {
          success,
          failure,
          failureParams,
        })
      }
    >
      {children(local, setLocal)}
    </CommitDrawer>
  );
}
